import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  Shield,
  Zap,
  Globe,
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  Building2,
  Smartphone,
} from 'lucide-react';

interface WalletOnboardingProps {
  onStartOnboarding: () => void;
  isLoading?: boolean;
}

const ONBOARDING_STEPS = [
  {
    icon: Building2,
    titleKey: 'wallet.onboarding.steps.createAccount.title',
    descriptionKey: 'wallet.onboarding.steps.createAccount.description',
  },
  {
    icon: CreditCard,
    titleKey: 'wallet.onboarding.steps.addPayoutMethod.title',
    descriptionKey: 'wallet.onboarding.steps.addPayoutMethod.description',
  },
  {
    icon: Wallet,
    titleKey: 'wallet.onboarding.steps.getPaid.title',
    descriptionKey: 'wallet.onboarding.steps.getPaid.description',
  },
];

const FEATURES = [
  {
    icon: Zap,
    titleKey: 'wallet.onboarding.features.fast.title',
    descriptionKey: 'wallet.onboarding.features.fast.description',
  },
  {
    icon: Shield,
    titleKey: 'wallet.onboarding.features.secure.title',
    descriptionKey: 'wallet.onboarding.features.secure.description',
  },
  {
    icon: Globe,
    titleKey: 'wallet.onboarding.features.global.title',
    descriptionKey: 'wallet.onboarding.features.global.description',
  },
];

const PAYOUT_METHODS = [
  { name: 'IBAN / SEPA', icon: Building2 },
  { name: 'PayPal', icon: Smartphone },
  { name: 'Wise', icon: Globe },
];

export function WalletOnboarding({ onStartOnboarding, isLoading }: WalletOnboardingProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/90 via-primary to-primary/80 p-8 md:p-12 text-primary-foreground">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative z-10 max-w-2xl">
          <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
            <Wallet className="w-3 h-3 mr-1" />
            {t('wallet.onboarding.badge')}
          </Badge>

          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {t('wallet.onboarding.title')}
          </h1>

          <p className="text-lg text-primary-foreground/80 mb-8 max-w-xl">
            {t('wallet.onboarding.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              size="lg"
              variant="secondary"
              onClick={onStartOnboarding}
              disabled={isLoading}
              className="font-semibold"
            >
              {isLoading ? (
                <Clock className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-5 h-5 mr-2" />
              )}
              {t('wallet.onboarding.cta')}
            </Button>

            <div className="flex items-center gap-2 text-sm text-primary-foreground/70">
              <Shield className="w-4 h-4" />
              {t('wallet.onboarding.secureNote')}
            </div>
          </div>
        </div>

        {/* Floating Illustration */}
        <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2">
          <div className="relative">
            <div className="w-48 h-48 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-6 transform rotate-6">
              <Wallet className="w-full h-full text-white/40" />
            </div>
            <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div>
        <h2 className="text-xl font-semibold mb-6">{t('wallet.onboarding.howItWorks')}</h2>

        <div className="grid gap-4 md:grid-cols-3">
          {ONBOARDING_STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <Card key={index} className="relative overflow-hidden group hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  {/* Step Number */}
                  <div className="absolute top-4 right-4 text-4xl font-bold text-muted/20">
                    {index + 1}
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>

                    <div className="space-y-1">
                      <h3 className="font-semibold">{t(step.titleKey)}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t(step.descriptionKey)}
                      </p>
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < ONBOARDING_STEPS.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-0.5 bg-border" />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid gap-6 md:grid-cols-3">
        {FEATURES.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div
              key={index}
              className="flex items-start gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-background flex items-center justify-center shadow-sm">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-medium mb-1">{t(feature.titleKey)}</h4>
                <p className="text-sm text-muted-foreground">
                  {t(feature.descriptionKey)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Supported Payout Methods */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-semibold mb-1">{t('wallet.onboarding.payoutMethods.title')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('wallet.onboarding.payoutMethods.description')}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {PAYOUT_METHODS.map((method, index) => {
                const Icon = method.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-sm font-medium"
                  >
                    <Icon className="w-4 h-4" />
                    {method.name}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom CTA */}
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">
          {t('wallet.onboarding.readyToStart')}
        </p>
        <Button size="lg" onClick={onStartOnboarding} disabled={isLoading}>
          {isLoading ? (
            <Clock className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <ArrowRight className="w-5 h-5 mr-2" />
          )}
          {t('wallet.onboarding.cta')}
        </Button>
      </div>
    </div>
  );
}
